import React, { useEffect, useState, useRef } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import { Spin, DatePicker } from "antd"
import { Chart, registerables } from "chart.js"
import dayjs from "dayjs"
import { useSelector } from "react-redux"
import { Analytics } from "../Apis/analyticsApi"
import { getUserCookies } from "../utils/Cookies"
import { jwtDecode } from "jwt-decode"
import { effectiveHighestRole } from "../utils/modulePermissionSidebar"
import { translations } from "../utils/Translation/staticTranslation"
import Dropdown from "../components/Common/Dropdown"
import StoreDrawer from "../components/Common/StoreDrawer"
import "../styles/Pages/dashboard.css"

Chart.register(...registerables)

const { RangePicker } = DatePicker

const LINE_COLORS = [
  "#8B5CF6",
  "#22C55E",
  "#3B82F6",
  "#F59E0B",
  "#EF4444",
  "#06B6D4",
  "#EC4899",
  "#F97316",
]

// ── Grouping helpers (same logic as AreaChartWithZoom) ───────────────────────

/** Group flat days array into weekly buckets, summing each event key */
const groupByWeek = (days, eventKeys) => {
  const weeks = []
  let bucket = []

  days.forEach((day, idx) => {
    bucket.push(day)
    if ((idx + 1) % 7 === 0 || idx === days.length - 1) {
      const first = bucket[0]
      const last = bucket[bucket.length - 1]
      const label = `${dayjs(first.day).format("D MMM")} - ${dayjs(last.day).format("D MMM")}`
      const entry = { label }
      eventKeys.forEach((k) => {
        entry[k] = bucket.reduce((sum, d) => sum + (d[k] || 0), 0)
      })
      weeks.push(entry)
      bucket = []
    }
  })
  return weeks
}

/** Group flat days array into monthly buckets, summing each event key */
const groupByMonth = (days, eventKeys) => {
  const map = {}
  days.forEach((day) => {
    const key = dayjs(day.day).format("YYYY-MM")
    const label = dayjs(day.day).format("MMMM YYYY")
    if (!map[key]) {
      map[key] = { label }
      eventKeys.forEach((k) => (map[key][k] = 0))
    }
    eventKeys.forEach((k) => {
      map[key][k] += day[k] || 0
    })
  })
  return Object.values(map)
}

/**
 * Transform the `days` array from the API into Chart.js-ready data,
 * grouping by day / week / month depending on the active filter — exactly
 * like AreaChartWithZoom does for clicks/impressions.
 */
const buildChartData = (days, filterType, startDate, endDate) => {
  if (!Array.isArray(days) || days.length === 0) return null

  const eventKeys = Object.keys(days[0]).filter((k) => k !== "day")

  // Decide grouping
  let grouping = "day"
  if (filterType === "last7days") {
    grouping = "day"
  } else if (filterType === "last30days" || filterType === "currentMonth") {
    grouping = "week"
  } else if (filterType === "last90days") {
    grouping = "month"
  } else if (filterType === "customdate" && startDate && endDate) {
    const duration = dayjs(endDate).diff(dayjs(startDate), "day")
    if (duration <= 7)       grouping = "day"
    else if (duration <= 60) grouping = "week"
    else                     grouping = "month"
  }

  // Build grouped rows
  let rows = []
  if (grouping === "day") {
    rows = days.map((d) => {
      const entry = { label: dayjs(d.day).format("D MMM") }
      eventKeys.forEach((k) => (entry[k] = d[k] || 0))
      return entry
    })
  } else if (grouping === "week") {
    rows = groupByWeek(days, eventKeys)
  } else {
    rows = groupByMonth(days, eventKeys)
  }

  const labels = rows.map((r) => r.label)
  const datasets = eventKeys.map((key, i) => ({
    label: key,
    data: rows.map((r) => r[key] || 0),
    backgroundColor: `${LINE_COLORS[i % LINE_COLORS.length]}aa`,
    borderColor: LINE_COLORS[i % LINE_COLORS.length],
    borderWidth: 1.5,
    borderRadius: 4,
    barPercentage: 0.6,
    categoryPercentage: 0.8,
  }))

  return { labels, datasets }
}

// ── Chart component ───────────────────────────────────────────────────────────
const SallaEventsChart = ({ days, filterType, startDate, endDate, lang }) => {
  const canvasRef = useRef(null)
  const chartInstance = useRef(null)

  useEffect(() => {
    if (!canvasRef.current) return

    chartInstance.current?.destroy()
    const chartData = buildChartData(days, filterType, startDate, endDate)

    // Empty state chart
    if (!chartData) {
      chartInstance.current = new Chart(canvasRef.current.getContext("2d"), {
        type: "bar",
        data: {
          labels: [lang === "ar" ? "لا توجد بيانات" : "No data"],
          datasets: [{ label: "", data: [0], backgroundColor: "rgba(200,200,200,0.3)", borderColor: "rgba(200,200,200,0.5)", borderWidth: 1 }],
        },
        options: {
          responsive: true, maintainAspectRatio: false,
          plugins: { legend: { display: false }, tooltip: { enabled: false } },
        },
      })
      return
    }

    chartInstance.current = new Chart(canvasRef.current.getContext("2d"), {
      type: "bar",
      data: { labels: chartData.labels, datasets: chartData.datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        plugins: {
          legend: {
            display: true,
            position: "top",
            labels: { padding: 16, font: { size: 12 }, usePointStyle: true, pointStyleWidth: 8 },
          },
          tooltip: {
            mode: "index",
            intersect: false,
            callbacks: {
              label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y.toLocaleString()}`,
            },
          },
        },
        scales: {
          x: {
            grid: { display: true, drawBorder: true },
            ticks: { font: { size: 12 }, color: "#6B7280" },
          },
          y: {
            beginAtZero: true,
            grid: { color: "#F3F4F6" },
            ticks: {
              font: { size: 12 },
              color: "#6B7280",
              precision: 0,
              callback: (v) => (v >= 1000 ? `${v / 1000}k` : v),
            },
          },
        },
        layout: { padding: { top: 10 } },
      },
    })

    return () => chartInstance.current?.destroy()
  }, [days, filterType, startDate, endDate])

  return (
    <div
      style={{
        background: "#fff",
        borderRadius: "8px",
        border: "1px solid #e4e5ec",
        padding: "20px 24px",
        marginTop: "24px",
      }}
    >
      <h3 className="text-141414 font-600" style={{ fontSize: "16px", marginBottom: "20px" }}>
        {lang === "ar" ? "الأحداث عبر الزمن" : "Events Over Time"}
      </h3>
      <div style={{ position: "relative", width: "100%", height: "400px" }}>
        <canvas ref={canvasRef} />
      </div>
    </div>
  )
}

// ── Main Page ─────────────────────────────────────────────────────────────────
const SallaAnalytics = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const lang = localStorage.getItem("language") || "ar"
  const currentLanguage = translations[lang]?.AnalyticsPages
  const dir = lang === "ar" ? "rtl" : "ltr"

  const token = getUserCookies()?.token
  const decoded = token ? jwtDecode(token) : null
  const highest_role = decoded?.highest_role
  const market_role = decoded?.market_role
  const user = useSelector((state) => state.user.user)
  const effectiveHighest = effectiveHighestRole(user, highest_role)

  const allStores = useSelector((state) => state.store.stores) ?? []
  const sallaStores = allStores.filter((s) => s.salla_merchant_id)

  const [selectedStore, setSelectedStore] = useState(null)
  const [drawerVisible, setDrawerVisible] = useState(false)

  const [filterType, setFilterType] = useState("last30days")
  const [selectedLabel, setSelectedLabel] = useState(
    lang === "ar" ? "آخر ٣٠ يومًا" : "Last 30 days"
  )
  const [startDate, setStartDate] = useState(null)
  const [endDate, setEndDate] = useState(null)

  const [loading, setLoading] = useState(false)
  const [data, setData] = useState(null)
  const [error, setError] = useState(null)

  // Auto-select first salla store
  useEffect(() => {
    if (sallaStores.length > 0 && !selectedStore) {
      setSelectedStore(sallaStores[0])
    }
  }, [sallaStores.length])

  // Initial fetch once store resolves
  useEffect(() => {
    if (selectedStore !== null || sallaStores.length === 0) {
      doFetch("last30days", null, null, selectedStore)
    }
  }, [selectedStore])

  // ── Core fetch ───────────────────────────────────────────────────────────────
  const doFetch = async (ft, sd, ed, store) => {
    if (ft === "customdate" && (!sd || !ed)) return

    setLoading(true)
    setError(null)
    try {
      const storeId = store?.salla_merchant_id ?? null
      const response = await Analytics.getSallaAnalytics(ft, sd, ed, storeId)
      if (response.status === 200) setData(response.data)
    } catch (err) {
      console.error("Salla analytics error:", err)
      setError(lang === "ar" ? "فشل تحميل البيانات" : "Failed to load analytics data")
    } finally {
      setLoading(false)
    }
  }

  // ── Filter handlers ───────────────────────────────────────────────────────────
  const LABEL_TO_FILTER = {
    "Last 7 days": "last7days",   "آخر ٧ أيام":   "last7days",
    "Last 30 days": "last30days", "آخر ٣٠ يومًا": "last30days",
    "Last 90 days": "last90days", "آخر ٩٠ يومًا": "last90days",
    "Custom Range": "customdate", "نطاق مخصص":    "customdate",
  }

  const handleDropdownChange = (label) => {
    const ft = LABEL_TO_FILTER[label] || "last30days"
    setSelectedLabel(label)
    setFilterType(ft)
    if (ft === "customdate") { setStartDate(null); setEndDate(null); return }
    setStartDate(null); setEndDate(null)
    doFetch(ft, null, null, selectedStore)
  }

  const handleCustomRangeChange = (dates) => {
    if (!dates || dates.length < 2) return
    const sd = dates[0].format("YYYY-MM-DD")
    const ed = dates[1].format("YYYY-MM-DD")
    setStartDate(sd); setEndDate(ed)
    doFetch("customdate", sd, ed, selectedStore)
  }

  const handleStoreSelect = (store) => {
    setSelectedStore(store)
    doFetch(filterType, startDate, endDate, store)
  }

  const disabledDate = (current) => current && current > dayjs().endOf("day")

  const dropdownOptions =
    lang === "ar"
      ? [
          { label: "آخر ٧ أيام", value: "آخر ٧ أيام" },
          { label: "آخر ٣٠ يومًا", value: "آخر ٣٠ يومًا" },
          { label: "آخر ٩٠ يومًا", value: "آخر ٩٠ يومًا" },
          { label: "نطاق مخصص", value: "نطاق مخصص" },
        ]
      : [
          { label: "Last 7 days", value: "Last 7 days" },
          { label: "Last 30 days", value: "Last 30 days" },
          { label: "Last 90 days", value: "Last 90 days" },
          { label: "Custom Range", value: "Custom Range" },
        ]

  const normalizePath = (p) => { const t = (p || "").replace(/\/+$/, ""); return t === "" ? "/" : t }
  const currentPath = normalizePath(location.pathname)

  const eventCounts = data?.event_counts ?? {}
  const days = data?.days ?? []
  const storeName = selectedStore
    ? lang === "ar" ? selectedStore.store_name_arabic : selectedStore.store_name
    : lang === "ar" ? "اختر متجرًا" : "Select Store"

  return (
    <div dir={dir}>
      {/* ── Header ───────────────────────────────────────────────────────── */}
      <div className="d-flex justify-content-between align-items-center" style={{ paddingBottom: "15px" }}>
        <h1 className="text-141414 text-20 font-600 m-0">
          {currentLanguage?.welcome}{" "}
          {lang === "ar" ? user?.name_arabic : user?.name_english}
        </h1>

        <div className="d-flex align-items-center" style={{ gap: "12px", flexWrap: "wrap" }}>
          <div className="nav-switch">
            <button
              className={`navbar-tab ${currentPath === "/dashboard" ? "active" : ""}`}
              onClick={() => navigate("/dashboard")}
            >
              {lang === "ar" ? "لوحة التحكم" : "Dashboard"}
            </button>
            <button
              className={`navbar-tab ${currentPath === "/dashboard/salla-analytics" ? "active" : ""}`}
              onClick={() => navigate("/dashboard/salla-analytics")}
            >
              {lang === "ar" ? "تحليلات سلة" : "Salla Analytics"}
            </button>
          </div>

          {sallaStores.length > 1 && (
            <div className="select-store" onClick={() => setDrawerVisible(true)} style={{ cursor: "pointer" }}>
              <p className={`mb-0 ${lang === "ar" ? "ml-10" : "mr-10"}`}>{storeName}</p>
              <img src="/icons/arrowSelectionDark.svg" style={{ transform: lang === "ar" ? "rotateY(180deg)" : "none" }} alt="" />
            </div>
          )}

          <div className="d-flex align-items-center" style={{ gap: "8px" }}>
            {filterType === "customdate" && (
              <div className="custom-datepicker-wrapper">
                <RangePicker
                  allowClear={false}
                  disabledDate={disabledDate}
                  placement="bottomLeft"
                  value={startDate && endDate ? [dayjs(startDate), dayjs(endDate)] : []}
                  onChange={handleCustomRangeChange}
                  format="YYYY-MM-DD"
                />
              </div>
            )}
            <Dropdown
              selectedCategory={selectedLabel}
              width="160px"
              label={currentLanguage?.dropdown_1?.last_30_days}
              iconSrc="/icons/selectstoreanalytics.svg"
              options={dropdownOptions}
              onSelect={handleDropdownChange}
            />
          </div>
        </div>
      </div>

      {/* ── No salla stores ───────────────────────────────────────────────── */}
      {sallaStores.length === 0 && !loading && (
        <div style={{ background: "#fff8f3", border: "1px solid #FD853A40", borderRadius: "8px", padding: "20px 24px", color: "#92400e", fontSize: "14px" }}>
          {lang === "ar"
            ? "لا توجد متاجر مرتبطة بسلة. يرجى ربط متجرك بمنصة سلة أولاً."
            : "No stores are connected to Salla. Please link your store to Salla first."}
        </div>
      )}

      {/* ── Content ──────────────────────────────────────────────────────── */}
      {sallaStores.length > 0 && (
        loading ? (
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "300px" }}>
            <Spin size="large" />
          </div>
        ) : error ? (
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "200px" }}>
            <p style={{ color: "#ef4444" }}>{error}</p>
          </div>
        ) : (
          <>
            {/* ── Event Counts ─────────────────────────────────────────── */}
            {Object.keys(eventCounts).length > 0 && (
              <div style={{ background: "#fff", borderRadius: "8px", border: "1px solid #e4e5ec", padding: "20px 24px", marginTop: "24px" }}>
                <h3 className="text-141414 font-600" style={{ fontSize: "16px", marginBottom: "16px" }}>
                  {lang === "ar" ? "عدد الأحداث" : "Event Counts"}
                </h3>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: "12px" }}>
                  {Object.entries(eventCounts).map(([event, count], i) => (
                    <div
                      key={event}
                      style={{
                        display: "flex", alignItems: "center", justifyContent: "space-between",
                        padding: "14px 16px", borderRadius: "8px",
                        background: `${LINE_COLORS[i % LINE_COLORS.length]}0d`,
                        border: `1px solid ${LINE_COLORS[i % LINE_COLORS.length]}30`,
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: "10px", overflow: "hidden" }}>
                        <span style={{ width: 10, height: 10, borderRadius: "50%", background: LINE_COLORS[i % LINE_COLORS.length], flexShrink: 0, display: "inline-block" }} />
                        <span style={{ fontSize: "13px", color: "#374151", fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={event}>
                          {event}
                        </span>
                      </div>
                      <span style={{ fontSize: "20px", fontWeight: 700, color: LINE_COLORS[i % LINE_COLORS.length], marginLeft: "12px", flexShrink: 0 }}>
                        {count.toLocaleString()}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── Smart grouped chart ──────────────────────────────────── */}
            <SallaEventsChart
              days={days}
              filterType={filterType}
              startDate={startDate}
              endDate={endDate}
              lang={lang}
            />
          </>
        )
      )}

      {/* ── Store Drawer ─────────────────────────────────────────────────── */}
      <StoreDrawer
        visible={drawerVisible}
        onClose={() => setDrawerVisible(false)}
        stores={sallaStores}
        selectedStore={selectedStore}
        onSelectStore={handleStoreSelect}
        AllFilterOption={currentLanguage?.stores}
      />
    </div>
  )
}

export default SallaAnalytics
